/**
 * Decisions and answers (docs/design/03 §4; engine `table/state/decisions.ts`). Setup decisions are used
 * in every tier; the tier-2/3 kinds are shaped by the rules layers and may still change (with a protocol
 * version bump).
 */
import { z } from 'zod';
import {
  BattlefieldIdSchema,
  CardRefSchema,
  ChainItemIdSchema,
  DecisionIdSchema,
  DomainSchema,
  JsonObjectSchema,
  ObjectIdSchema,
  PlayerIdSchema,
  ZoneRefSchema,
  int,
  text,
} from './primitives';

export const PromptSchema = z.strictObject({
  key: text(80),
  params: z.record(z.string().max(40), z.union([z.string().max(200), z.number(), z.boolean(), z.null()])),
});

const base = {
  id: DecisionIdSchema,
  seq: int(0, Number.MAX_SAFE_INTEGER),
  player: PlayerIdSchema,
  prompt: PromptSchema,
  source: ObjectIdSchema.nullable(),
  cancellable: z.boolean(),
  timer: z.enum(['decision', 'none']),
};

export const CostSpecSchema = z.strictObject({
  energy: int(0, 99),
  power: z.array(z.strictObject({ domain: z.union([DomainSchema, z.literal('any')]), n: int(0, 99) })),
  other: z.array(JsonObjectSchema),
});

export const ActionHintsSchema = z.strictObject({
  destinations: z.array(ZoneRefSchema),
  firstTargets: z.array(ObjectIdSchema),
  cost: CostSpecSchema.nullable(),
});

export const LegalActionSchema = z.discriminatedUnion('type', [
  z.strictObject({
    id: text(64),
    type: z.literal('play'),
    object: ObjectIdSchema,
    from: ZoneRefSchema,
    hints: ActionHintsSchema,
  }),
  z.strictObject({
    id: text(64),
    type: z.literal('activate'),
    object: ObjectIdSchema,
    ability: int(0, 99),
    hints: ActionHintsSchema,
  }),
  z.strictObject({
    id: text(64),
    type: z.literal('standardMove'),
    object: ObjectIdSchema,
    destinations: z.array(ZoneRefSchema),
  }),
  z.strictObject({
    id: text(64),
    type: z.literal('hide'),
    object: ObjectIdSchema,
    destinations: z.array(BattlefieldIdSchema),
  }),
  z.strictObject({ id: text(64), type: z.literal('pass'), label: z.enum(['pass', 'endTurn', 'passFocus']) }),
]);

export const PaymentSourceSchema = z.strictObject({
  id: text(64),
  object: ObjectIdSchema.nullable(),
  uses: z.array(z.enum(['exhaust', 'recycle', 'spend', 'activate'])),
});

export const PaymentChoiceSchema = z.strictObject({
  source: text(64),
  use: z.enum(['exhaust', 'recycle', 'spend', 'activate']),
});

export const BattlefieldOptionSchema = z.strictObject({
  id: text(64),
  card: CardRefSchema.nullable(),
  battlefield: BattlefieldIdSchema.nullable(),
});

/**
 * One recipient of combat damage with the engine's constraints (CR 467.2.c): lethal threshold (at least 1;
 * `Number.MAX_SAFE_INTEGER` when all of it would be prevented) and assignment group (0 [Tank], 1, 2 [Backline]).
 */
export const DamageRecipientSchema = z.strictObject({
  object: ObjectIdSchema,
  lethal: int(1, Number.MAX_SAFE_INTEGER),
  group: z.union([z.literal(0), z.literal(1), z.literal(2)]),
});

export const DecisionSchema = z.discriminatedUnion('kind', [
  z.strictObject({ ...base, kind: z.literal('mulligan'), options: z.array(ObjectIdSchema), max: int(0, 10) }),
  z.strictObject({
    ...base,
    kind: z.literal('chooseBattlefield'),
    purpose: z.enum(['setup', 'destination']),
    options: z.array(BattlefieldOptionSchema),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('priority'),
    window: z.enum(['main', 'showdownFocus', 'chain']),
    actions: z.array(LegalActionSchema),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('chooseTargets'),
    item: ChainItemIdSchema.nullable(),
    instruction: int(0, 999).nullable(),
    options: z.array(ObjectIdSchema),
    min: int(0, 99),
    max: int(0, 99),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('chooseObjects'),
    options: z.array(ObjectIdSchema),
    min: int(0, 99),
    max: int(0, 99),
    zone: ZoneRefSchema.nullable(),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('chooseMode'),
    modes: z.array(z.strictObject({ id: text(64), prompt: PromptSchema })),
    min: int(0, 9),
    max: int(0, 9),
  }),
  z.strictObject({
    ...base,
    kind: z.literal('payCost'),
    cost: CostSpecSchema,
    sources: z.array(PaymentSourceSchema),
    suggested: z.array(PaymentChoiceSchema).nullable(),
  }),
  z.strictObject({ ...base, kind: z.literal('yesNo') }),
  z.strictObject({ ...base, kind: z.literal('chooseNumber'), min: int(0, 999), max: int(0, 999) }),
  z.strictObject({ ...base, kind: z.literal('order'), items: z.array(text(64)) }),
  z.strictObject({
    ...base,
    kind: z.literal('assignDamage'),
    amount: int(0, 999),
    targets: z.array(ObjectIdSchema),
    recipients: z.array(DamageRecipientSchema).nullable(),
  }),
]);

/** Answers (client → server): bounded so a hostile client can't send huge payloads. */
export const AnswerSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('mulligan'), setAside: z.array(ObjectIdSchema).max(10) }),
  z.strictObject({ kind: z.literal('chooseBattlefield'), option: text(64) }),
  z.strictObject({ kind: z.literal('priority'), action: text(64) }),
  z.strictObject({ kind: z.literal('chooseTargets'), targets: z.array(ObjectIdSchema).max(60) }),
  z.strictObject({ kind: z.literal('chooseObjects'), objects: z.array(ObjectIdSchema).max(60) }),
  z.strictObject({ kind: z.literal('chooseMode'), modes: z.array(text(64)).max(9) }),
  z.strictObject({ kind: z.literal('payCost'), payments: z.array(PaymentChoiceSchema).max(60) }),
  z.strictObject({ kind: z.literal('yesNo'), yes: z.boolean() }),
  z.strictObject({ kind: z.literal('chooseNumber'), n: int(0, 999) }),
  z.strictObject({ kind: z.literal('order'), order: z.array(text(64)).max(60) }),
  z.strictObject({
    kind: z.literal('assignDamage'),
    assignment: z.array(z.strictObject({ target: ObjectIdSchema, amount: int(0, 999) })).max(60),
  }),
  z.strictObject({ kind: z.literal('cancel') }),
]);
